import { Product } from "@/data/products";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Check } from "lucide-react";

interface ProductInfoModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

export const ProductInfoModal = ({
  product,
  isOpen,
  onClose,
}: ProductInfoModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl sm:text-3xl text-gold mb-2">
            {product.name}
          </DialogTitle>
          <DialogDescription className="text-lg sm:text-xl italic text-gold/80">
            {product.tagline}
          </DialogDescription>
        </DialogHeader>

        {/* Product Image */}
        <div className="relative w-full h-48 sm:h-64 rounded-xl overflow-hidden mb-6">
          <img
            src={product.lifestyleImage}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Storytelling Description */}
        <div className="prose prose-sm max-w-none mb-6">
          {product.longDescription.split("\n\n").map((paragraph, idx) => (
            <p
              key={idx}
              className="text-foreground leading-relaxed mb-4 text-base sm:text-lg"
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Benefits */}
        <div className="mb-6">
          <h4 className="font-bold text-lg sm:text-xl mb-4 flex items-center gap-2">
            <span className="text-2xl">✨</span> Voordelen
          </h4>
          <ul className="space-y-2">
            {product.benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-foreground text-sm sm:text-base">
                  {benefit}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Ingredients (only for tea products) */}
        {product.ingredients && product.ingredients.length > 0 && (
          <div className="mb-6">
            <h4 className="font-bold text-lg sm:text-xl mb-4 flex items-center gap-2">
              <span className="text-2xl">🌿</span> Ingrediënten
            </h4>
            <ul className="space-y-2">
              {product.ingredients.map((ingredient, idx) => {
                const text = typeof ingredient === 'string'
                  ? ingredient
                  : (ingredient as any)?.name ?? (ingredient as any)?.label ?? String(ingredient);
                return (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-gold">•</span>
                    <span className="text-foreground text-sm sm:text-base">
                      {text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Usage */}
        {product.usage && (
          <div className="bg-card/50 rounded-xl p-4 sm:p-6 mb-6 border border-gold/20">
            <h4 className="font-bold text-lg sm:text-xl mb-3 flex items-center gap-2">
              <span className="text-2xl">🫖</span> Gebruik
            </h4>
            <div className="text-foreground leading-relaxed text-sm sm:text-base">
              {product.usage.split("\n").map((line, idx) => (
                <p key={idx} className="mb-2">
                  {line}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Content Info */}
        {product.contentInfo && (
          <div className="mb-6">
            <h4 className="font-bold text-lg sm:text-xl mb-3 flex items-center gap-2">
              <span className="text-2xl">📦</span> Inhoud
            </h4>
            <p className="text-foreground text-sm sm:text-base">
              {product.contentInfo}
            </p>
          </div>
        )}

        {/* Footer with CTA */}
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto min-h-[48px]"
          >
            Sluiten
          </Button>
          <Button
            onClick={onClose}
            className="btn-hero w-full sm:w-auto min-h-[48px]"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Naar productkaart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
